import styled from 'styled-components';

/**
 * The row inside a panel. It scrolls rather than wraps, and it hides its own
 * scrollbar: the bar would sit inside the box, under the rounded corner.
 * Dragging and the wheel still move it - see `useDragScroll`.
 */
export const AmountsScroller = styled.div`
  display: flex;
  align-items: stretch;
  overflow-x: auto;

  ::-webkit-scrollbar {
    display: none;
  }
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

/**
 * One figure inside the panel, sized to its own text: a payout line like
 * `10% - 3 rules - 4 exceptions` must not wrap, and a short tax must not be
 * padded out to the width of the longest one.
 */
export const AmountCell = styled.div<{
  $clickable?: boolean;
  $disabled?: boolean;
}>`
  /* Grows into the room the panel has to spare, never shrinks below its own
  text: a half empty box reads as missing data, a clipped figure as a bug. */
  flex: 1 0 auto;
  display: flex;
  align-items: center;
  padding: 12px 20px;
  min-height: 70px;
  white-space: nowrap;
  opacity: ${({ $disabled }) => ($disabled ? 0.5 : 1)};
  cursor: ${({ $clickable }) => ($clickable ? 'pointer' : 'default')};
  transition: background 0.2s ease;

  /* Figures in the same box still need a line between them. What separates
  the payouts from the taxes is now the box itself. */
  & + & {
    border-left: 1px solid ${({ theme }) => theme.mediumGray};
  }

  :hover {
    background: ${({ theme, $clickable }) =>
      $clickable ? theme.background : 'transparent'};
  }

  @media (max-width: 800px) {
    padding: 12px 16px;
    min-height: 60px;
  }
`;

/**
 * The payout box: it takes whatever the taxes leave, because it is the one
 * that always has more to show than fits.
 */
export const AmountsMain = styled.div`
  flex: 1 1 0;
  min-width: 0;

  @media (max-width: 800px) {
    /* The row stacks here, where a basis of 0 would be a height, not a width. */
    flex: none;
    width: 100%;
  }
`;

/**
 * The tax box: only as wide as the taxes in it, up to half the row. A location
 * usually has one or two, and stretching them to half the screen leaves a
 * white field that reads as missing data.
 */
export const AmountsAside = styled.div`
  flex: 0 1 auto;
  min-width: 0;
  max-width: 50%;

  /* A location with no taxes has no second box, and an empty column would
  still take the row's gap and leave the payouts short of the edge. */
  &:empty {
    display: none;
  }

  @media (max-width: 800px) {
    flex: none;
    width: 100%;
    max-width: none;
  }
`;
